import { useRef, useEffect, useMemo } from 'react';

/**
 * A one-shot macrotask (`setTimeout(0)`) you can arm, cancel, and track pending state.
 * Re-arming cancels the task armed before it, and the task auto-cancels on unmount.
 *
 * `setTimeout`, not `queueMicrotask`: a microtask drains before the current task yields, so it
 * would still run before browser-dispatched events like `focus`. A macrotask runs after them,
 * which is the whole reason to defer.
 *
 * @note "Macrotask" an informal name contrasting with "microtask". The HTML spec just calls
 * it a "task", but the naming contrast makes it clear it should be compared with a microtask.
 */
function useMacrotask() {
    const id = useRef();
    useEffect(() => () => clearTimeout(id.current), []);
    return useMemo(() => ({
        get pending() {
            return id.current != null;
        },
        schedule(fn) {
            clearTimeout(id.current);
            id.current = setTimeout(() => {
                // Clear before firing so a handler reading `pending` mid-fire sees the task as done.
                id.current = undefined;
                fn();
            }, 0);
        },
        cancel() {
            clearTimeout(id.current);
            id.current = undefined;
        },
    }), []);
}

export { useMacrotask };
//# sourceMappingURL=use-macrotask.js.map
